import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, X, ChevronDown, Mail, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const FAQ = [
  { q: "How do I run a broker query?",
    a: "Open any judgment and click 'Run Query' from the profile header or use the Play icon on the judgment list. Select brokers or a bundle, then confirm." },
  { q: "What is the Automatic Stay?",
    a: "Under 11 U.S.C. § 362, when a debtor files for bankruptcy, all enforcement actions are legally halted. See Bankruptcy Watch for details." },
  { q: "How do subscription tiers work?",
    a: "Bronze runs auto-queries every 12 months, Silver every 6 months, Gold every 3 months. Per-query broker costs apply separately from your subscription." },
  { q: "Can I import a spreadsheet of judgments?",
    a: "Not yet — bulk import ships in a future release. For now, add judgments one at a time." },
  { q: "Is my data secure?",
    a: "All data is encrypted at rest (AES-256) and in transit (TLS 1.3). SSNs and financial fields use field-level encryption with access logging." },
];

const APP_VERSION = "0.7.0";

const FaqRow = ({ item, idx, openIdx, setOpenIdx }) => {
  const isOpen = openIdx === idx;
  return (
    <div className="border-b border-border-default last:border-0" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpenIdx(isOpen ? -1 : idx)}
        className="w-full flex items-center justify-between py-3 text-left"
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="text-white text-[14px] font-medium">{item.q}</span>
        <ChevronDown size={14} className={cn("text-text-secondary shrink-0 transition-transform", isOpen && "rotate-180")} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
            data-testid={`faq-answer-${idx}`}
          >
            <p className="text-text-secondary text-[14px] pb-3 leading-6">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HelpModal = ({ open, onClose }) => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="help-modal"
          >
            <div
              className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-10 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <HelpCircle size={24} className="text-accent-pink" />
                  <h2 className="text-white font-bold text-[20px]">Help & Support</h2>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 text-text-secondary hover:text-white"
                  data-testid="help-close"
                >
                  <X size={16} />
                </button>
              </div>

              <section className="mb-6" data-testid="help-faq">
                <div className="jea-caption mb-2">Frequently Asked Questions</div>
                <div>
                  {FAQ.map((item, idx) => (
                    <FaqRow key={idx} item={item} idx={idx} openIdx={openIdx} setOpenIdx={setOpenIdx} />
                  ))}
                </div>
              </section>

              <section className="mb-6 rounded-[8px] bg-bg-tertiary p-4" data-testid="help-contact">
                <div className="jea-caption mb-2">Contact Support</div>
                <a
                  href="mailto:support@judgment-ai.local"
                  className="inline-flex items-center gap-2 text-accent-pink text-[14px] font-semibold hover:underline"
                  data-testid="help-contact-link"
                >
                  <Mail size={14} /> support@judgment-ai.local
                </a>
                <p className="text-text-secondary text-[13px] mt-2 leading-6">
                  Reply within 1 business day. Include your debtor ID or query run ID if reporting an issue.
                </p>
              </section>

              <section className="mb-6" data-testid="help-docs">
                <a
                  href="/docs"
                  className="inline-flex items-center gap-2 text-accent-pink text-[14px] font-semibold hover:underline"
                  data-testid="help-docs-link"
                >
                  View full documentation <ExternalLink size={12} />
                </a>
              </section>

              <div className="pt-4 border-t border-border-default text-[12px] text-text-muted" data-testid="help-version">
                Judgment Enforcement AI — build {APP_VERSION}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HelpModal;
